import { Box, CircularProgress, Grid } from "@mui/material";
import {
  InnerWrapper,
  MainContainer,
  RangePicker,
  TitleAndActionBox,
} from "../../../common";
import {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LanguageContext } from "../../../../context/LanguageContext";
import { ParagraphText, UBButton } from "../../../../Libraries";
import {
  CalendarIconNew,
  DownloadIconBlack,
} from "../../../../assets/icons/icon";
import RendorList from "./RendorList";
import { useNavigate } from "react-router-dom";
import styles from "./styles.module.css";
import { useBusinessOverview } from "../../../../hooks/useBusinessOverview";
import { useAuthContext } from "../../../../context/AuthContext";
import {
  formatRange,
  mapCategoryToList,
  getStatementDateConfig,
} from "../../../../utils";
import {
  DEFAULT_DATE_FILTERS_EXPORT,
  DEFAULT_DATE_FILTERS_REPORTS,
} from "../../../../constants";
import moment from "moment";
import useDownlaodPdf from "../../../../hooks/useDownlaodPdf";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { PDF_DOWNLOAD_NEW } from "../../../../apis/configuration";

const { minDate, maxDate } = getStatementDateConfig();
const defaultStart = moment().subtract(29, "days").startOf("day").toDate();
const defaultEnd = moment().endOf("day").toDate();

const getStoredFilter = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem("bo_cf_filter"));
    if (!saved?.start_date || !saved?.end_date) return null;
    return { start_date: new Date(saved.start_date), end_date: new Date(saved.end_date) };
  } catch { return null; }
};

const CashFlow = () => {
  const [I18n] = useContext(LanguageContext);
  const { business, user } = useAuthContext();
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [dateFilterOpen, setIsDateFilterOpen] = useState(false);
  const [loading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    const saved = getStoredFilter();
    return { start_date: saved?.start_date || defaultStart, end_date: saved?.end_date || defaultEnd };
  });
  const navigate = useNavigate();
  const isApiCalled = useRef(true);

  const {
    handleGetCashflowStatement,
    money_out_st,
    expenses_category_cf,
    net_cash_flow_st,
    money_in_st,
    sales_category_cf,
    cf_statement_loading,
  } = useBusinessOverview({ business_id: business?.id, navigate });

  const { onDownload, downloadLoading } = useDownlaodPdf(
    user,
    PDF_DOWNLOAD_NEW,
  );

  useEffect(() => {
    const saved = getStoredFilter();
    handleGetCashflowStatement({ startDate: saved?.start_date || defaultStart, endDate: saved?.end_date || defaultEnd });
    isApiCalled.current = false;
  }, []);

  // Category names are resolved in the selected language when the statement
  // is built, so rebuild it for the same date range when the language changes
  const lastI18nRef = useRef(I18n);
  useEffect(() => {
    if (lastI18nRef.current === I18n) return;
    lastI18nRef.current = I18n;
    const { start_date, end_date } = selectedDate;
    handleGetCashflowStatement(
      start_date && end_date ? { startDate: start_date, endDate: end_date } : undefined,
    );
  }, [I18n]);

  const handleApplyFilter = (startDate, endDate) => {
    setSelectedDate({ start_date: startDate, end_date: endDate });
    setIsDateFilterOpen(false);
    if (!startDate || !endDate) {
      handleGetCashflowStatement();
    } else {
      sessionStorage.setItem("bo_cf_filter", JSON.stringify({ start_date: startDate, end_date: endDate }));
      handleGetCashflowStatement({ startDate, endDate });
    }
  };

  // const handleDownloadStatement = (startDate, endDate) => {
  //   triggerLogEvent(analytics, "cashflow_report_download");
  //   setIsExportOpen(false);
  //   let paramsForReport = {
  //     template_name: "accountreport_new.hbs",
  //     business_id: business.id,
  //     phone_number: user?.phone_number,
  //     type: "cash-flow",
  //     pdf: true,
  //     start_date: startDate ? moment(startDate).format("DD MMM YYYY") : "",
  //     end_date: endDate ? moment(endDate).format("DD MMM YYYY") : "",
  //   };

  //   onDownload(paramsForReport, () => {}, navigate);
  // };

  const summary = [
    { label: "gross_cash_inflow", value: money_in_st, isSign: true },
    {
      label: "gross_cash_outflow",
      value: money_out_st,
      isSign: true,
      isMinusSign: true,
    },
    {
      label: "net_cash_change",
      value: net_cash_flow_st,
      isMunusSign: net_cash_flow_st < 0 ? true : false,
      isSign: net_cash_flow_st < 0 ? true : false,
    },
  ];

  const salesList = useMemo(
    () => mapCategoryToList(sales_category_cf || {}),
    [sales_category_cf],
  );

  const expenseList = useMemo(
    () => mapCategoryToList(expenses_category_cf || {}),
    [expenses_category_cf],
  );

  return (
    <MainContainer fluid className="quickSalePage">
      <Grid container>
        <Grid item md={12}>
          <TitleAndActionBox data={{ isBack: true, title: I18n.cash_flow }} />
          <InnerWrapper className={`innerWrapper ${styles.mainWrapper}`}>
            {cf_statement_loading || isApiCalled.current ? (
              <Box className="d-flex justify-content-center align-items-center h-100">
                <CircularProgress size="30px" />
              </Box>
            ) : (
              <Fragment>
                <Box
                  className={`d-flex align-items-center justify-content-between ${styles.header}`}
                >
                  <ParagraphText
                    className={"fontSize18 fontWeight500 newFontColor"}
                  >
                    {I18n.cash_flow}
                  </ParagraphText>
                  <Box display={"flex"} gap={"12px"}>
                    <Box className={styles.dateContainer}>
                      <UBButton
                        id="cashflow_filter_btn"
                        title={formatRange(selectedDate, I18n)}
                        className={styles.exportButton}
                        onClick={() => setIsDateFilterOpen(!dateFilterOpen)}
                        spinnerColor={"black"}
                        iconComponent={
                          loading ? null : (
                            <CalendarIconNew width="12px" height="12px" />
                          )
                        }
                        loading={loading}
                      />

                      {dateFilterOpen ? (
                        <RangePicker
                          onFilterDate={(startDate, endDate) => {
                            handleApplyFilter(startDate, endDate);
                          }}
                          isOpen={dateFilterOpen}
                          setIsOpen={setIsExportOpen}
                          showDirectPicker={true}
                          rangeValue={
                            selectedDate?.start_date && selectedDate?.end_date
                              ? [selectedDate.start_date, selectedDate.end_date]
                              : []
                          }
                          noClearBtn={true}
                          dateFilters={DEFAULT_DATE_FILTERS_REPORTS}
                          minDate={minDate}
                          maxDate={maxDate}
                        />
                      ) : null}
                    </Box>
                    {/* <Box className={styles.dateContainer}>
                      <UBButton
                        title={I18n.export}
                        className={styles.exportButton}
                        onClick={() => setIsExportOpen(!isExportOpen)}
                        spinnerColor={"black"}
                        loading={downloadLoading}
                        iconComponent={
                          loading ? null : (
                            <DownloadIconBlack width="12px" height="12px" />
                          )
                        }
                        loading={loading}
                      />

                      {isExportOpen ? (
                        <RangePicker
                          onFilterDate={handleDownloadStatement}
                          isOpen={isExportOpen}
                          setIsOpen={setIsExportOpen}
                          showDirectPicker={true}
                          rangeValue={[]}
                          noClearBtn={true}
                          dateFilters={DEFAULT_DATE_FILTERS_EXPORT}
                        />
                      ) : null}
                    </Box> */}
                  </Box>
                </Box>
                <RendorList title={"summary"} list={summary} />
                {salesList?.length ? (
                  <RendorList
                    title={"sales"}
                    list={salesList}
                    seeMoreLess={salesList.length > 3}
                    onClick={(category) =>
                      navigate("/CategorizedList", {
                        state: { category, type: "Sale" },
                      })
                    }
                  />
                ) : null}
                {expenseList?.length ? (
                  <RendorList
                    title={"expenses"}
                    list={expenseList}
                    seeMoreLess={expenseList.length > 3}
                    onClick={(category) =>
                      navigate("/CategorizedList", {
                        state: { category, type: "Expense" },
                      })
                    }
                  />
                ) : null}
              </Fragment>
            )}
          </InnerWrapper>
        </Grid>
      </Grid>
    </MainContainer>
  );
};
export default CashFlow;
